import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { setAcademicOfferingsList } from './academicOfferingsSlice';

import {
  AcademicOffering,
  AcademicOfferingsResponse,
  EducationLevel,
  Period,
} from '../interfaces/academicOfferings.interfaces';
import { PeriodsResponse } from '../interfaces/periods.interface';
import { EducationLevelsResponse } from '../../education-levels/interfaces/educationLevels.interfaces';
import { AccessState } from '../../../../types';

export const academicOfferingTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['AcademicOfferings', 'EducationLevelsAvailables'],
});
// Define a service using a base URL and expected endpoints
export const academicOfferingsApi = academicOfferingTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all Academic Offerings
     */
    getAcademicOfferings: builder.query<
      AcademicOfferingsResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        typeSort?: string;
        sort?: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage, typeSort, sort }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<AcademicOfferingsResponse>('/offers', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: typeSort || 'desc',
              sort: sort || 'id',
              search,
            },
          });

          dispatch(setAcademicOfferingsList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'AcademicOfferings', id } as const)),
              { type: 'AcademicOfferings', id: 'LIST' },
            ]
          : [{ type: 'AcademicOfferings', id: 'LIST' }],
    }),

    /**
     * @GET Academic Offering by id
     */
    getAcademicOffering: builder.query<
      AcademicOffering,
      { profileId?: number; academicOfferingId: number }
    >({
      queryFn: async ({ profileId, academicOfferingId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<AcademicOffering>(`/offers/${academicOfferingId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'AcademicOfferings', profileId }],
    }),

    /**
     * @GET Period Academic Offering by id
     */
    getPeriodAcademicOffering: builder.query<
      PeriodsResponse,
      {
        profileId?: number;
        academicOfferingId: string;
        campusId?: string;
        current?: boolean;
        isAdmin?: boolean;
      }
    >({
      queryFn: async (
        { profileId, academicOfferingId, campusId, current, isAdmin },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get(`/offers/${academicOfferingId}/periods`, {
            params: {
              user_profile_id,
              ...(campusId && { campus: campusId }),
              ...(current && { current }), //si se envia current la repuesta es un array de periodos
              ...(isAdmin && { isAdmin }),
              type_sort: 'desc',
            },
          });

          let newData: Period[] = [];
          if (current) newData = Array.isArray(data) ? data : [];

          return { data: current ? newData : data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [{ type: 'AcademicOfferings', id: 'LIST' }],
    }),

    /**
     * @GET all careers availables by offer id
     */
    getAvailablesCareersByOffer: builder.query<
      EducationLevel[],
      { profileId: Number; academicOfferingId: number }
    >({
      queryFn: async ({ profileId, academicOfferingId }) => {
        try {
          const { data } = await mainApi.get<EducationLevel[]>(
            `/offers/${academicOfferingId}/education-levels/career`,
            {
              params: {
                user_profile_id: profileId,
                data: 'all',
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [
        { type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' },
        { type: 'AcademicOfferings', id: 'LIST' },
      ],
    }),

    /**
     * @GET Career by Academic Offering id
     */
    getCareerbyAcademicOfferId: builder.query<
      EducationLevelsResponse,
      {
        profileId: number;
        academicOfferingId: string;
        page: number;
        perPage: number;
        search: string;
        group_area_id?: string;
      }
    >({
      queryFn: async ({ profileId, academicOfferingId, page, perPage, search, group_area_id }) => {
        try {
          const { data } = await mainApi.get(
            `/offers/${academicOfferingId}/education-levels/career`,
            {
              params: {
                user_profile_id: profileId,
                page,
                size: perPage,
                type_sort: 'desc',
                search,
                ...(group_area_id && { group_area_id }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'AcademicOfferings', profileId }],
    }),
    /**
     * @GET Career by Academic Offering id and filter
     */
    getCareerbyAcademicOfferIdAndSearch: builder.query<
      EducationLevelsResponse,
      {
        profileId: number;
        academicOfferingId: string;
        page: number;
        perPage: number;
        search: string;
        group_area_id?: string;
      }
    >({
      queryFn: async ({ profileId, academicOfferingId, page, perPage, search, group_area_id }) => {
        try {
          const { data } = await mainApi.get(
            `/offers/${academicOfferingId}/education-levels/career/filter`,
            {
              params: {
                user_profile_id: profileId,
                page,
                size: perPage,
                type_sort: 'desc',
                search,
                ...(group_area_id && { group_area_id }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'AcademicOfferings', profileId }],
    }),
    /**
     * @POST create Academic Offering
     */
    addAcademicOffering: builder.mutation<
      AcademicOffering,
      { profileId: number; academicOfferingPayload: Partial<AcademicOffering> }
    >({
      queryFn: async ({ profileId, academicOfferingPayload }) => {
        try {
          const { data } = await mainApi.post<AcademicOffering>('/offers', {
            user_profile_id: profileId,
            ...academicOfferingPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'AcademicOfferings', id: 'LIST' }],
    }),

    /**
     * @PUT update Academic Offering
     */
    updateAcademicOffering: builder.mutation<
      AcademicOffering,
      {
        profileId: number;
        academicOfferingId: number;
        academicOfferingPayload: Partial<AcademicOffering>;
      }
    >({
      queryFn: async ({ profileId, academicOfferingId, academicOfferingPayload }) => {
        try {
          const { data } = await mainApi.put<AcademicOffering>(`/offers/${academicOfferingId}`, {
            user_profile_id: profileId,
            ...academicOfferingPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'AcademicOfferings', id: 'LIST' }],
    }),

    /**
     * @DELETE remove Academic Offering
     */
    deleteAcademicOffering: builder.mutation<
      AcademicOffering,
      { profileId: number; academicOfferingId: number }
    >({
      queryFn: async ({ profileId, academicOfferingId }) => {
        try {
          const { data } = await mainApi.delete(`offers/${academicOfferingId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'AcademicOfferings', id: 'LIST' }],
    }),

    /**
     * @GET offers availables
     */
    getAvailablesOffers: builder.query<
      AcademicOffering[],
      { profileId?: number; off_description?: String; ficha?: string }
    >({
      queryFn: async ({ profileId, off_description, ficha }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get('/offers', {
            params: {
              user_profile_id,
              data: 'all',
              off_description,
              ficha,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'AcademicOfferings', id: 'LIST' }],
    }),

    /**
     * @GET offer by id education levels info
     */
    getInfoOffer: builder.query<
      EducationLevel[],
      { profileId: number; offerId: string; filter?: boolean }
    >({
      queryFn: async ({ profileId, offerId, filter }) => {
        try {
          const { data } = await mainApi.get<AcademicOffering>(`/offers/${offerId}`, {
            params: {
              user_profile_id: profileId,
              ...(filter && { filter }),
            },
          });

          return { data: Array.isArray(data.education_levels) ? data.education_levels : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { offerId }) => [
        // revisar casos donde tenga objetos relacionados anidados y a ese objeto relacionado
        // se le mute el registro no se revalide este (ej: si se usa el endpoint para sacar sus niveles
        // educativos y en niveles educativos se muta un registor relacionado con una determinada oferta,
        // esa oferta puede no actualizar sus datos relacionados al no invalidar el tag de la oferta
        // (u otro endpoint) por id)
        { type: 'AcademicOfferings', offerId },
        { type: 'AcademicOfferings', id: 'LIST' },
        { type: 'AcademicOfferings', id: 'INFO_OFFER' },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAcademicOfferingsQuery,
  useGetPeriodAcademicOfferingQuery,
  useAddAcademicOfferingMutation,
  useDeleteAcademicOfferingMutation,
  useGetAcademicOfferingQuery,
  useUpdateAcademicOfferingMutation,
  useGetCareerbyAcademicOfferIdQuery,
  useGetCareerbyAcademicOfferIdAndSearchQuery,
  useGetAvailablesCareersByOfferQuery,
  useGetAvailablesOffersQuery,
  useLazyGetAcademicOfferingQuery,
  useGetInfoOfferQuery,
} = academicOfferingsApi;

//////////////////////
